/**
 * THE PAGER for the server-paginated doctrine. It states the window in row numbers — "1–50 of
 * 22,257" — because the count IS the toolbar count on a server config, and offers first / prev /
 * next / last plus the four page sizes the API accepts (500 is its maximum).
 */
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "../ui/button";

export const PAGE_SIZES = [50, 100, 200, 500] as const;
export type PageSize = (typeof PAGE_SIZES)[number];

export interface PagerProps {
  /** Zero-based. */
  page: number;
  pageSize: number;
  total: number;
  onPage: (next: number) => void;
  onPageSize?: (next: PageSize) => void;
}

/** "1–50 of 22,257"; "0 of 0" on an empty set. */
export function pagerLabel(page: number, pageSize: number, total: number): string {
  if (total === 0) return "0 of 0";
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, total);
  return `${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}`;
}

export function Pager({ page, pageSize, total, onPage, onPageSize }: PagerProps) {
  const lastPage = total === 0 ? 0 : Math.ceil(total / pageSize) - 1;
  const atStart = page <= 0;
  const atEnd = page >= lastPage;

  return (
    <div className="flex items-center gap-2xs">
      <span className="type-caption text-muted-foreground tabular-nums">
        {pagerLabel(page, pageSize, total)}
      </span>

      {onPageSize ? (
        <select
          aria-label="Rows per page"
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.currentTarget.value) as PageSize)}
          className="border-border bg-background type-caption h-[var(--control-height-md)] rounded-[var(--radius-sm)] border px-2xs"
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size} / page
            </option>
          ))}
        </select>
      ) : null}

      <div className="flex items-center">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label="First page"
          disabled={atStart}
          onClick={() => onPage(0)}
        >
          <ChevronFirst aria-hidden className="size-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label="Previous page"
          disabled={atStart}
          onClick={() => onPage(page - 1)}
        >
          <ChevronLeft aria-hidden className="size-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label="Next page"
          disabled={atEnd}
          onClick={() => onPage(page + 1)}
        >
          <ChevronRight aria-hidden className="size-4" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label="Last page"
          disabled={atEnd}
          onClick={() => onPage(lastPage)}
        >
          <ChevronLast aria-hidden className="size-4" />
        </Button>
      </div>
    </div>
  );
}
